import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import siteMessage from "@insite/client-framework/SiteMessage";
import setAddToListModalIsOpen from "@insite/client-framework/Store/Components/AddToListModal/Handlers/SetAddToListModalIsOpen";
import cleanupUploadData from "@insite/client-framework/Store/Components/OrderUpload/Handlers/CleanupUploadData";
import clearProduct from "@insite/client-framework/Store/Components/ProductSelector/Handlers/ClearProduct";
import { getProductSelector } from "@insite/client-framework/Store/Components/ProductSelector/ProductSelectorSelectors";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import addCartLines from "@insite/client-framework/Store/Data/Carts/Handlers/AddCartLines";
import loadCurrentCart from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCurrentCart";
import { getProductState } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import addToWishList from "@insite/client-framework/Store/Data/WishLists/Handlers/AddToWishList";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import addProduct from "@insite/client-framework/Store/Pages/QuickOrder/Handlers/AddProduct";
import clearProducts from "@insite/client-framework/Store/Pages/QuickOrder/Handlers/ClearProducts";
import translate from "@insite/client-framework/Translate";
import { CartLineCollectionModel, ProductModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ProductAddedToCartMessage from "@insite/content-library/Components/ProductAddedToCartMessage";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import getProductCategories from "@waytek/Handlers/Utilities/CustomGetProductCategories";
import { trackAddToCartEvent, trackQuickOrderEvent } from "@waytek/Handlers/Utilities/CustomTracking";
import processCopyPaste from "@waytek/Store/CommonHandlers/ProcessCopyPaste";
import setQuickOrderInformation from "@waytek/Store/CommonHandlers/SetQuickOrderInformation";
import { CustomState } from "@waytek/Store/Reducers";
import QuickOrderCopyAndPasteProcess from "@waytek/Widgets/QuickOrder/QuickOrderCopyAndPasteProcess";
import React, { useContext, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: CustomState) => {
    const { productInfos } = state.pages.quickOrder;
    const { productCopyPaste, productSelectQty, isLoadingMultipleProducts, isAddedMultipleProductsCompleted } =
        state.productInformationState;
    const { selectedProductInfo } = getProductSelector(state);
    const product = getProductState(state, selectedProductInfo?.productId).value as ProductModel;
    const settingsCollection = getSettingsCollection(state);
    const disableAddToOrder = !selectedProductInfo && (productCopyPaste ?? "").length === 0;
    const { isUpdatingCart } = state.context;

    return {
        productInfos,
        products: productInfos.map(o => getProductState(state, o.productId).value),
        productCopyPaste,
        selectedProductInfo,
        product,
        productSelectQty,
        isLoadingMultipleProducts,
        isAddedMultipleProductsCompleted,
        disableAddToOrder,
        orderUploadPageLink: getPageLinkByPageType(state, "OrderUploadPage"),
        cartPageLink: getPageLinkByPageType(state, "CartPage"),
        allowMultipleWishLists: settingsCollection.wishListSettings.allowMultipleWishLists,
        showAddToCartConfirmationDialog: settingsCollection.productSettings.showAddToCartConfirmationDialog,
        isUpdatingCart,
    };
};

const mapDispatchToProps = {
    addProduct,
    processCopyPaste,
    setQuickOrderInformation,
    clearProduct,
    addToWishList,
    setAddToListModalIsOpen,
    addCartLines: makeHandlerChainAwaitable(addCartLines),
    getProductCategories,
    clearProducts,
    loadCurrentCart,
    cleanupUploadData,
};

type Props = WidgetProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps> & HasHistory;

export interface QuickOrderActionsForDesktopStyles {
    wrapper?: InjectableCss;
    outlineButton?: ButtonPresentationProps;
    solidButton?: ButtonPresentationProps;
}

export const quickOrderActionsForDesktopStyles: QuickOrderActionsForDesktopStyles = {
    wrapper: {
        css: css`
            align-items: center;
            display: flex;
            justify-content: flex-end;
            gap: 20px;
        `,
    },
    outlineButton: {
        variant: "secondary",
    },
    solidButton: {
        variant: "primary",
    },
};

const styles = quickOrderActionsForDesktopStyles;

const QuickOrderActionsForDesktop = ({
    productInfos,
    products,
    productCopyPaste,
    selectedProductInfo,
    product,
    productSelectQty,
    isLoadingMultipleProducts,
    disableAddToOrder,
    orderUploadPageLink,
    cartPageLink,
    allowMultipleWishLists,
    showAddToCartConfirmationDialog,
    history,
    addProduct,
    processCopyPaste,
    setQuickOrderInformation,
    clearProduct,
    addToWishList,
    setAddToListModalIsOpen,
    addCartLines,
    getProductCategories,
    clearProducts,
    loadCurrentCart,
    isUpdatingCart,
    isAddedMultipleProductsCompleted,
    cleanupUploadData,
}: Props) => {
    const toasterContext = useContext(ToasterContext);

    const [allQtysIsValid, setAllQtysIsValid] = useState(false);
    const productsExist = productInfos && productInfos.length > 0;
    const canAddSomeToList = products.some(o => o?.canAddToWishlist);

    useEffect(() => {
        setAllQtysIsValid(productInfos.every(o => o.qtyOrdered > 0));
    }, [productInfos]);

    const uploadOrderClickHandler = () => {
        orderUploadPageLink && history.push(orderUploadPageLink.url);
    };

    const addProductToOrder = () => {
        if (productCopyPaste) {
            const productsFromCopy = productCopyPaste
                .split("\n")
                .filter(p => p.trim() !== "")
                .map(p => {
                    const productInfo = p.split(/[\t\s,]/);
                    return {
                        partNumber: productInfo[0],
                        qty: productInfo.length > 1 ? +productInfo[productInfo.length - 1] : 0,
                    };
                });
            processCopyPaste({
                products: productsFromCopy,
            });
        } else {
            const productInfo = { ...selectedProductInfo } as ProductInfo;
            productInfo.qtyOrdered = productSelectQty ?? 0;
            addProduct({ productInfo, product });
            toasterContext.addToast({ body: translate("Item Added"), messageType: "success" });
            setQuickOrderInformation({
                setQty: false,
                shouldClear: true,
            });
            // XNG-Changes:
            trackQuickOrderEvent();
            clearProduct();
        }
    };

    const addToListClickHandler = () => {
        const productInfosToAdd = productInfos.filter(o => products.find(p => p?.id === o.productId)?.canAddToWishlist);
        if (!allowMultipleWishLists) {
            addToWishList({
                productInfos: productInfosToAdd,
                onSuccess: () => {
                    toasterContext.addToast({ body: siteMessage("Lists_ProductAdded"), messageType: "success" });
                },
            });
            return;
        }

        setAddToListModalIsOpen({ modalIsOpen: true, productInfos: productInfosToAdd });
    };

    const addAllToCartClickHandler = async () => {
        const reversedList = [...productInfos].reverse();
        const cartLinesCollection = (await addCartLines({ productInfos: reversedList })) as CartLineCollectionModel;

        if (cartLinesCollection.cartLines) {
            const productIds = cartLinesCollection.cartLines.map(cartLine => cartLine.productId) as string[];
            getProductCategories({
                ProductIds: productIds,
                onComplete: result => {
                    if (result && result.apiResult) {
                        setTimeout(() => {
                            trackAddToCartEvent(
                                cartLinesCollection.cartLines ?? [],
                                result.apiResult.productProperties,
                            );
                        }, 300);
                    }
                },
            });
        }

        if (showAddToCartConfirmationDialog) {
            const isQtyAdjusted = cartLinesCollection.cartLines?.some(cartLine => cartLine.isQtyAdjusted) ?? false;

            toasterContext.addToast({
                body: <ProductAddedToCartMessage isQtyAdjusted={isQtyAdjusted} multipleProducts={true} />,
                messageType: "success",
            });
        }
        clearProducts();
        cartPageLink && history.push(cartPageLink.url);
        loadCurrentCart({ shouldLoadFullCart: true });
    };

    // VERNDALE CUSTOMIZATION START - automatically add order to cart if all quantities are valid
    useEffect(() => {
        if (productsExist && allQtysIsValid) {
            addAllToCartClickHandler();
            clearProducts();
            cleanupUploadData();
            setQuickOrderInformation({
                setQty: false,
                shouldClear: true,
            });
        }
    }, [isAddedMultipleProductsCompleted]);
    // VERNDALE CUSTOMIZATION END

    return (
        <StyledWrapper {...styles.wrapper}>
            <Button
                {...styles.outlineButton}
                onClick={uploadOrderClickHandler}
                data-test-selector="quickOrder_UploadOrder"
                disabled={isLoadingMultipleProducts || isUpdatingCart}
            >
                {translate("Upload Order")}
            </Button>

            <Button
                {...styles.outlineButton}
                onClick={addProductToOrder}
                disabled={disableAddToOrder || isLoadingMultipleProducts}
                data-test-selector="quickOrder_AddToOrder"
            >
                {translate("Add to Order")}
            </Button>

            {productsExist && !isUpdatingCart && (
                <>
                    <Button
                        {...styles.outlineButton}
                        onClick={addToListClickHandler}
                        disabled={!allQtysIsValid || !canAddSomeToList || isLoadingMultipleProducts}
                        data-test-selector="quickOrder_AddToList"
                    >
                        {translate("Add to List")}
                    </Button>

                    <Button
                        {...styles.solidButton}
                        onClick={addAllToCartClickHandler}
                        disabled={!allQtysIsValid || isLoadingMultipleProducts}
                        data-test-selector="quickOrder_AddToCart"
                    >
                        {translate("Add to Cart & Checkout")}
                    </Button>
                </>
            )}

            <QuickOrderCopyAndPasteProcess />
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(
        mapStateToProps,
        mapDispatchToProps,
    )(withHistory(QuickOrderActionsForDesktop as any) as any) as any,
    definition: {
        group: "Quick Order",
        allowedContexts: ["QuickOrderPage"],
        displayName: "Actions [For Desktop]",
    },
};

export default widgetModule;
